import { useState, useEffect, useMemo } from 'react';
import { api } from '../../../services/api'; // Ya no necesitas apiClient
import CardGrid from '../componentes/CardGrid';
import DeleteConfirm from '../componentes/DeleteConfirm';
import Badge from '../componentes/Badge';
import SearchBar from '../componentes/SearchBar';
import ProductoModal from '../componentes/ProductoModal';
import { getImageUrl } from '../utils/imageUrl';
import { Plus, Tag } from 'lucide-react';

const formatearPrecio = (v) =>
  new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(parseFloat(v) || 0);

const ProductosPanel = () => {
  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchProductos = async () => {
    try {
      setLoading(true);
      const res = await api.getProductos();
      setProductos(res.data?.data || res.data || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProductos(); }, []);

  const filtrados = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return productos;
    return productos.filter(p => p.titulo?.toLowerCase().includes(q));
  }, [productos, search]);

  const stats = useMemo(() => ({
    total: productos.length,
    activos: productos.filter(p => p.activo === 1).length,
    oferta: productos.filter(p => p.oferta === 1).length,
  }), [productos]);

  const handleEdit = (p) => {
    // Al editar, pasamos el objeto completo con las URLs de imágenes para mostrar en el modal
    setSelected({
      ...p,
      imagen1: getImageUrl(p.imagen1),
      imagen2: getImageUrl(p.imagen2),
      imagen3: getImageUrl(p.imagen3),
    });
    setShowEdit(true);
  };

  const handleCreate = () => {
    setSelected(null);
    setShowEdit(true);
  };

  const handleDelete = (id) => {
    setSelected(productos.find(p => p.id === id));
    setShowDelete(true);
  };

  // 🟢 handleSave recibe formData directamente desde el modal
  const handleSave = async (formData) => {
    try {
      setSaving(true);
      if (selected) {
        // Actualizar: enviar FormData con PUT
        await api.updateProducto(selected.id, formData);
      } else {
        // Crear: enviar FormData con POST
        await api.createProducto(formData);
      }
      setShowEdit(false);
      fetchProductos();
    } catch (err) {
      alert(err.response?.data?.error || err.message || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await api.deleteProducto(selected.id);
      setShowDelete(false);
      fetchProductos();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al eliminar');
    }
  };

  return (
    <div className="productos-panel">
      {/* Encabezado */}
      <div className="panel-header">
        <h1>Productos</h1>
        <button className="sp-btn sp-btn--primary" onClick={handleCreate}>
          <Plus size={18} /> Nuevo
        </button>
      </div>

      {/* Estadísticas */}
      <div className="stats-row">
        <Badge variant="primary">Total: {stats.total}</Badge>
        <Badge variant="success">Activos: {stats.activos}</Badge>
        <Badge variant="warning">En oferta: {stats.oferta}</Badge>
      </div>

      {/* Búsqueda */}
      <div className="search-wrapper">
        <SearchBar value={search} onChange={setSearch} placeholder="Buscar producto por título..." />
      </div>

      {/* Grid */}
      <CardGrid
        items={filtrados}
        loading={loading}
        error={error}
        emptyMessage="No se encontraron productos."
        columns="repeat(auto-fill, minmax(240px, 1fr))"
        renderCard={(p) => (
          <div className="product-card">
            <div className="product-image">
              <img
                src={getImageUrl(p.imagen1)}
                alt={p.titulo}
                onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = '/assets/smartpet-default.jpg'; }}
              />
              {p.oferta === 1 && <span className="offer-badge">OFERTA</span>}
            </div>
            <div className="product-body">
              <h3>{p.titulo}</h3>
              <p className="product-desc">{p.descripcion || 'Sin descripción'}</p>
              <div className="product-footer">
                <span className="product-price">{formatearPrecio(p.precio)}</span>
                <Badge variant={p.activo === 1 ? 'success' : 'danger'} icon={Tag}>
                  {p.activo === 1 ? 'Activo' : 'Inactivo'}
                </Badge>
              </div>
              <div className="product-actions">
                <button className="sp-btn sp-btn--light" onClick={() => handleEdit(p)}>
                  Editar
                </button>
                <button className="sp-btn sp-btn--danger" onClick={() => handleDelete(p.id)}>
                  Eliminar
                </button>
              </div>
            </div>
          </div>
        )}
      />

      {/* Modales */}
      {showEdit && (
        <ProductoModal
          isOpen={showEdit}
          onClose={() => setShowEdit(false)}
          onSave={handleSave}
          initialValues={selected}
          loading={saving}
        />
      )}

      {showDelete && (
        <DeleteConfirm
          message={`¿Eliminar el producto "${selected?.titulo || ''}"?`}
          onConfirm={confirmDelete}
          onCancel={() => setShowDelete(false)}
        />
      )}

      {/* Estilos (igual que antes) */}
      <style jsx>{`
        .productos-panel {
          padding: 0.5rem 0;
        }
        .panel-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
          flex-wrap: wrap;
          gap: 0.75rem;
        }
        .panel-header h1 {
          font-size: 1.5rem;
          font-weight: 800;
          margin: 0;
          color: #1a1a2e;
        }
        .stats-row {
          display: flex;
          gap: 0.75rem;
          margin-bottom: 1rem;
          flex-wrap: wrap;
        }
        .search-wrapper {
          margin-bottom: 1.25rem;
        }
        .product-card {
          background: #ffffff;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
          transition: box-shadow 0.25s, transform 0.15s;
          height: 100%;
          display: flex;
          flex-direction: column;
        }
        .product-card:hover {
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.10);
          transform: translateY(-2px);
        }
        .product-image {
          position: relative;
          background: #f8fafc;
          height: 150px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .product-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .offer-badge {
          position: absolute;
          top: 8px;
          left: 8px;
          background: #ec4899;
          color: #ffffff;
          font-size: 0.7rem;
          font-weight: 800;
          padding: 0.2rem 0.55rem;
          border-radius: 999px;
          letter-spacing: 0.3px;
        }
        .product-body {
          padding: 1rem;
          flex: 1;
          display: flex;
          flex-direction: column;
        }
        .product-body h3 {
          margin: 0 0 0.25rem 0;
          font-size: 1rem;
          font-weight: 800;
          color: #1a1a2e;
        }
        .product-desc {
          font-size: 0.8rem;
          color: #6b7280;
          margin: 0 0 0.5rem 0;
          min-height: 2.2em;
          line-height: 1.4;
        }
        .product-footer {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin: 0.25rem 0 0.75rem 0;
        }
        .product-price {
          font-weight: 800;
          font-size: 1.05rem;
          color: #6366f1;
        }
        .product-actions {
          display: flex;
          gap: 0.4rem;
          margin-top: auto;
        }
        .product-actions button {
          flex: 1;
          min-height: 36px;
          font-size: 0.82rem;
          font-weight: 500;
        }
        .sp-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.4rem;
          padding: 0.5rem 1rem;
          border: none;
          border-radius: 8px;
          font-weight: 600;
          font-size: 0.9rem;
          cursor: pointer;
          transition: all 0.2s;
          text-decoration: none;
        }
        .sp-btn--primary {
          background: #6366f1;
          color: #ffffff;
        }
        .sp-btn--primary:hover {
          background: #4f46e5;
          box-shadow: 0 4px 12px rgba(99, 102, 241, 0.4);
        }
        .sp-btn--light {
          background: #f3f4f6;
          color: #374151;
        }
        .sp-btn--light:hover {
          background: #e5e7eb;
        }
        .sp-btn--danger {
          background: #ef4444;
          color: #ffffff;
        }
        .sp-btn--danger:hover {
          background: #dc2626;
        }
      `}</style>
    </div>
  );
};

export default ProductosPanel;